<script lang="ts">
  /**
   * Within reach — F15.
   *
   * A shortlist, not a search box. The places are the ones already in the trip
   * book, banded by how far they actually are, and matched against the weekends
   * that are actually free. `05-features.md`: a shortlist gets a decision, a
   * search engine gets a closed tab.
   *
   * Two honesties are load-bearing here and both are easy to lose:
   *
   *  · **Nothing is estimated.** A place carries a drive time because somebody
   *    typed one. Places without one are listed under their own heading with an
   *    em dash rather than converted from kilometres at an invented average speed.
   *  · **A band is matched to a weekend, not to a wish.** Four hours each way is
   *    not a Saturday. Selecting a weekend does not hide the far bands — it says,
   *    in words, what that weekend can carry, which is the part that stops the
   *    same disappointing plan being made twice.
   */
  import { Icon, Panel, dateZA } from "@steyncd/steyn-design";
  import { loadCached } from "../lib/offline.svelte";
  import { driveTime, type Shortlist } from "../lib/trips";

  let { onopen }: { onopen: (tripId: string) => void } = $props();

  let data = $state<Shortlist | null>(null);
  let loading = $state(true);
  let error = $state<string | null>(null);
  let cachedAt = $state<string | null>(null);
  /** Index into the free weekends. Null means "any weekend". */
  let chosen = $state<number | null>(null);

  async function load() {
    loading = true;
    const r = await loadCached<Shortlist>("/shortlist");
    data = r.data;
    error = r.error;
    cachedAt = r.fresh ? null : r.at;
    loading = false;
    // Land on the next free weekend rather than on "any": the question somebody
    // opens this screen with is almost always about a specific weekend.
    if (chosen === null && r.data) {
      const i = r.data.weekends.findIndex((w) => w.free);
      chosen = i >= 0 ? i : null;
    }
  }
  $effect(() => {
    void load();
  });

  const weekend = $derived(chosen == null ? null : (data?.weekends[chosen] ?? null));

  const places = $derived((data?.bands ?? []).reduce((n, b) => n + b.places.length, 0));
  const freeCount = $derived((data?.weekends ?? []).filter((w) => w.free).length);

  /** The whole screen in one sentence — the part a household actually reads. */
  const sentence = $derived.by(() => {
    if (!data) return "";
    const unknown = data.unknown.length;
    const long = data.weekends.filter((w) => w.free && w.long).length;
    return (
      `${places} ${places === 1 ? "place" : "places"} with a drive time recorded, ` +
      `${freeCount} free ${freeCount === 1 ? "weekend" : "weekends"} in the next sixteen, ` +
      `${long ? `${long} of them long. ` : "none of them long. "}` +
      (unknown ? `${unknown} more ${unknown === 1 ? "place has" : "places have"} no drive time recorded.` : "Every place has a drive time.")
    );
  });

  /** Does this band fit the weekend that is selected? A sentence, not a colour. */
  function verdict(minNights: number | null): { ok: boolean; text: string } {
    if (!weekend || minNights == null) return { ok: true, text: "" };
    if (weekend.nights >= minNights) return { ok: true, text: `Fits ${weekend.nights} nights.` };
    return {
      ok: false,
      text: `Needs ${minNights} nights. This weekend has ${weekend.nights}.`,
    };
  }
</script>

<div class="wrap">
  <section class="card">
    <div class="head">
      <span class="kicker"><Icon name="car" size={13} /> Within reach of Pretoria</span>
      <span class="sub num">{places} places</span>
    </div>
    <p class="sentence">{sentence}</p>
    {#if cachedAt}
      <p class="banner banner--quiet">
        <Icon name="pause" size={13} />
        <span><strong>No signal.</strong> Showing the shortlist as it was on {dateZA(cachedAt)}.</span>
      </p>
    {/if}
  </section>

  <Panel {loading} {error} isEmpty={!loading && !error && places === 0 && (data?.unknown.length ?? 0) === 0} onretry={load} rows={4}>
    {#snippet empty()}
      Nothing to shortlist yet. Add a trip in the book and put a drive time on it — the moment one place has a drive
      time, this screen has an answer. Nothing here is ever estimated from a map.
    {/snippet}

    <!-- ── the weekends, which is what does the filtering ───────────────── -->
    <section class="card">
      <div class="head">
        <span class="kicker"><Icon name="calendar" size={13} /> Which weekend</span>
        <span class="sub">{freeCount} free of {data?.weekends.length ?? 0}</span>
      </div>
      <div class="weeks">
        <button class="wk" class:wk--on={chosen === null} onclick={() => (chosen = null)}>
          <span class="wk__t">Any weekend</span>
          <span class="wk__s">No filtering</span>
        </button>
        {#each data?.weekends ?? [] as w, i (w.from)}
          <button
            class="wk"
            class:wk--on={chosen === i}
            class:wk--taken={!w.free}
            onclick={() => (chosen = i)}
            aria-pressed={chosen === i}
          >
            <span class="wk__t">
              {#if w.long}<Icon name="check" size={11} />{/if}
              {dateZA(w.from)} → {dateZA(w.to)}
            </span>
            <span class="wk__s">{w.label}</span>
            {#if w.free}
              <span class="wk__m wk__m--ok"><Icon name="check" size={11} /> free</span>
            {:else}
              <span class="wk__m wk__m--warn"><Icon name="alert" size={11} /> taken — {w.takenBy?.name}</span>
            {/if}
          </button>
        {/each}
      </div>
    </section>

    <!-- ── the bands ────────────────────────────────────────────────────── -->
    {#each data?.bands ?? [] as b (b.id)}
      {@const v = verdict(b.minNights)}
      <section class="card" class:card--off={!v.ok}>
        <div class="head">
          <span class="kicker">{b.label}</span>
          <span class="sub num">{b.places.length}</span>
        </div>
        <p class="fits">
          {#if v.ok}
            <span class="mark"><Icon name="dot" size={10} /> {b.fits}</span>
          {:else}
            <span class="mark mark--warn"><Icon name="alert" size={11} /> Too far for this weekend</span>
            <span class="mark">{v.text} {b.fits}</span>
          {/if}
        </p>

        {#if b.places.length === 0}
          <p class="sub pad">Nothing in the book at this distance yet.</p>
        {:else}
          {#each b.places as p (p.name)}
            <button class="row" onclick={() => onopen(p.tripId)}>
              <div class="grow">
                <div class="row__t">{p.name}</div>
                <div class="marks">
                  {#if p.kindLabel}<span class="mark"><Icon name="trip" size={11} /> {p.kindLabel.toLowerCase()}</span>{/if}
                  {#if p.timesBeen > 0}
                    <span class="mark"
                      ><Icon name="check" size={11} /> been {p.timesBeen}
                      {p.timesBeen === 1 ? "time" : "times"}{#if p.lastVisited}, last {dateZA(p.lastVisited)}{/if}</span
                    >
                  {:else}
                    <span class="mark"><Icon name="pause" size={11} /> never been</span>
                  {/if}
                  {#if p.bestRating != null}<span class="mark mark--ok"><Icon name="check" size={11} /> {p.bestRating}/5</span>{/if}
                </div>
              </div>
              <div class="dist">
                <span class="dist__v num">{driveTime(p.minutes)}</span>
                <span class="dist__k num">{p.km != null ? `${p.km} km` : "—"}</span>
              </div>
            </button>
          {/each}
        {/if}
      </section>
    {/each}

    {#if (data?.unknown.length ?? 0) > 0}
      <section class="card">
        <div class="head">
          <span class="kicker">No drive time recorded</span>
          <span class="sub num">{data?.unknown.length}</span>
        </div>
        <p class="fits">
          <span class="mark mark--warn"><Icon name="alert" size={11} /> Cannot be sorted</span>
          <span class="mark">These are not estimated from a map, and not silently converted from kilometres. Open one and type the drive time to place it.</span>
        </p>
        {#each data?.unknown ?? [] as p (p.name)}
          <button class="row" onclick={() => onopen(p.tripId)}>
            <div class="grow">
              <div class="row__t">{p.name}</div>
              <div class="marks">
                {#if p.kindLabel}<span class="mark"><Icon name="trip" size={11} /> {p.kindLabel.toLowerCase()}</span>{/if}
              </div>
            </div>
            <div class="dist">
              <span class="dist__v num">—</span>
              <span class="dist__k num">{p.km != null ? `${p.km} km` : "—"}</span>
            </div>
          </button>
        {/each}
      </section>
    {/if}

    <p class="note">{data?.note}</p>
  </Panel>
</div>

<style>
  .wrap { display: flex; flex-direction: column; gap: var(--sp-4); }
  .card {
    padding: var(--sp-4) var(--pad-x) var(--sp-4);
    border: 1px solid var(--line);
    border-radius: var(--r-surface);
    background: var(--s1);
  }
  /* A band that does not fit the chosen weekend is quieted, never hidden: the
     reason it does not fit is the useful part. */
  .card--off { opacity: 0.72; }
  .head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: var(--sp-3);
    margin-bottom: var(--sp-2);
  }
  .kicker { display: inline-flex; align-items: center; gap: 6px; }
  .sub { font-size: var(--fs-small); color: var(--mut); }
  .sentence { margin: 0; font-size: var(--fs-body); color: var(--tx2); }
  .pad { padding: var(--sp-2) 0; }
  .note { margin: 0; font-size: var(--fs-micro); color: var(--mut); }

  .banner {
    display: flex;
    align-items: flex-start;
    gap: 7px;
    margin: var(--sp-3) 0 0;
    padding: var(--sp-2) var(--sp-3);
    border-left: 2px solid var(--mut);
    border-radius: var(--r-control);
    background: var(--fill);
    font-size: var(--fs-small);
    color: var(--tx2);
  }
  .banner--quiet { border-left-color: var(--mut); }

  .weeks { display: grid; grid-template-columns: repeat(auto-fit, minmax(230px, 1fr)); gap: var(--sp-2); }
  .wk {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-height: var(--row-h);
    padding: var(--sp-3);
    text-align: left;
    border: 1px solid var(--line);
    border-radius: var(--r-control);
    color: var(--tx2);
  }
  .wk:hover { background: var(--card-hover); }
  .wk--on { box-shadow: inset 2px 0 0 var(--acc); background: var(--wash); color: var(--tx); }
  .wk--taken { opacity: 0.72; }
  .wk__t { display: inline-flex; align-items: center; gap: 5px; font-size: var(--fs-body); font-weight: 700; }
  .wk__s { font-size: var(--fs-micro); color: var(--mut); }
  .wk__m { display: inline-flex; align-items: center; gap: 4px; font-size: var(--fs-micro); font-weight: 700; color: var(--mut); }
  .wk__m--ok { color: var(--ok); }
  .wk__m--warn { color: var(--warn); }

  .fits { display: flex; flex-wrap: wrap; gap: var(--sp-2); margin: 0 0 var(--sp-2); }

  .row {
    display: flex;
    align-items: center;
    gap: var(--sp-3);
    width: 100%;
    min-height: var(--row-h);
    padding: var(--sp-2) var(--sp-3);
    text-align: left;
    border-bottom: 1px solid var(--line);
    border-radius: 0;
  }
  .row:hover { background: var(--card-hover); }
  .grow { flex: 1; min-width: 0; }
  .row__t { font-size: var(--fs-body); font-weight: 700; color: var(--tx); }
  .marks { display: flex; flex-wrap: wrap; gap: var(--sp-2); margin-top: 2px; }
  .mark { display: inline-flex; align-items: center; gap: 4px; font-size: var(--fs-micro); font-weight: 700; color: var(--mut); }
  .mark--warn { color: var(--warn); }
  .mark--ok { color: var(--ok); }

  .dist { display: flex; flex-direction: column; align-items: flex-end; gap: 1px; flex-shrink: 0; }
  .dist__v { font-family: var(--font-figure); font-size: var(--fs-lead); font-weight: 700; color: var(--tx); }
  .dist__k { font-size: var(--fs-micro); color: var(--mut); }
  .num { font-variant-numeric: tabular-nums; }
</style>
